import { useState, useMemo } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
} from 'recharts';

// Date range preset types
type DateRangePreset = 'last7days' | 'last30days' | 'thisMonth' | 'last3months' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Calculate date range based on preset selection
 */
function getDateRangeFromPreset(preset: DateRangePreset, customRange?: DateRange): DateRange {
  const now = new Date();
  const today = now.toISOString().split('T')[0];

  switch (preset) {
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'last3months': {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: today,
      };
    }
    case 'custom': {
      return customRange ?? {
        startDate: today,
        endDate: today,
      };
    }
  }
}

/**
 * Format date range for display
 */
function formatDateRangeDisplay(range: DateRange): string {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  return `${formatDate(range.startDate)} - ${formatDate(range.endDate)}`;
}

interface DateRangePickerProps {
  selectedPreset: DateRangePreset;
  customRange: DateRange;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomRangeChange: (range: DateRange) => void;
}

/**
 * DateRangePicker component with preset options and custom date selection.
 */
function DateRangePicker({
  selectedPreset,
  customRange,
  onPresetChange,
  onCustomRangeChange,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const presets: { key: DateRangePreset; label: string }[] = [
    { key: 'last7days', label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last3months', label: 'Last 3 Months' },
    { key: 'custom', label: 'Custom Range' },
  ];

  const displayText = selectedPreset === 'custom'
    ? formatDateRangeDisplay(customRange)
    : presets.find(p => p.key === selectedPreset)?.label ?? 'Select Range';

  const handlePresetSelect = (preset: DateRangePreset) => {
    onPresetChange(preset);
    if (preset !== 'custom') {
      setIsOpen(false);
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onCustomRangeChange({
      ...customRange,
      [field]: value,
    });
  };

  const handleApplyCustomRange = () => {
    setIsOpen(false);
  };

  return (
    <div style={datePickerStyles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={datePickerStyles.triggerButton}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px' }}>
          <path
            d="M5 1V3M11 1V3M2 6H14M3 2H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{displayText}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{ marginLeft: '8px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div style={datePickerStyles.backdrop} onClick={() => setIsOpen(false)} />
          <div style={datePickerStyles.dropdown}>
            <div style={datePickerStyles.presetSection}>
              {presets.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handlePresetSelect(preset.key)}
                  style={{
                    ...datePickerStyles.presetButton,
                    ...(selectedPreset === preset.key ? datePickerStyles.presetButtonActive : {}),
                  }}
                >
                  {selectedPreset === preset.key && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '8px' }}>
                      <path d="M2 7L5.5 10.5L12 4" stroke="#5A8A5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {preset.label}
                </button>
              ))}
            </div>

            {selectedPreset === 'custom' && (
              <div style={datePickerStyles.customSection}>
                <div style={datePickerStyles.dateInputGroup}>
                  <label style={datePickerStyles.dateLabel}>From</label>
                  <input
                    type="date"
                    value={customRange.startDate}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    style={datePickerStyles.dateInput}
                    max={customRange.endDate}
                  />
                </div>
                <div style={datePickerStyles.dateInputGroup}>
                  <label style={datePickerStyles.dateLabel}>To</label>
                  <input
                    type="date"
                    value={customRange.endDate}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    style={datePickerStyles.dateInput}
                    min={customRange.startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <button
                  onClick={handleApplyCustomRange}
                  style={datePickerStyles.applyButton}
                >
                  Apply
                </button>
              </div>
            )}

            <div style={datePickerStyles.rangeDisplay}>
              <span style={datePickerStyles.rangeDisplayLabel}>Selected:</span>
              <span style={datePickerStyles.rangeDisplayValue}>
                {formatDateRangeDisplay(getDateRangeFromPreset(selectedPreset, customRange))}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// DateRangePicker styles
const datePickerStyles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  triggerButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    backgroundColor: 'white',
    color: '#3D3A36',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    zIndex: 100,
    minWidth: '240px',
    overflow: 'hidden',
  },
  presetSection: {
    padding: '8px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  presetButton: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#5C564E',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
  },
  presetButtonActive: {
    backgroundColor: 'rgba(90, 138, 94, 0.08)',
    color: '#5A8A5E',
    fontWeight: 600,
  },
  customSection: {
    padding: '16px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  dateInputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  dateLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#9B9285',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  dateInput: {
    padding: '10px 12px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#3D3A36',
    outline: 'none',
  },
  applyButton: {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#5A8A5E',
    color: 'white',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    marginTop: '4px',
  },
  rangeDisplay: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
  },
  rangeDisplayLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  rangeDisplayValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
};

// Chart colors
const CHART_COLORS = [
  '#5A8A5E',  // Primary green
  '#6366f1',  // Purple
  '#3b82f6',  // Blue
  '#9B7B3C',  // Gold
  '#8B5A50',  // Brown/red
  '#9B9285',  // Gray
  '#C9A090',  // Tan
  '#706A62',  // Dark gray
];

// Format cost in dollars with 4 decimal places for precision
function formatCost(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}K`;
  }
  return `$${dollars.toFixed(4)}`;
}

// Format token count
function formatTokens(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

// Custom tooltip component
interface TooltipPayload {
  name: string;
  value: number;
  color: string;
  payload: Record<string, unknown>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
  valueFormatter?: (value: number) => string;
}

function CustomTooltip({ active, payload, label, valueFormatter = formatCost }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div style={tooltipStyles.container}>
      {label && <p style={tooltipStyles.label}>{label}</p>}
      {payload.map((entry, index) => (
        <p key={index} style={{ ...tooltipStyles.value, color: entry.color }}>
          {entry.name}: {valueFormatter(entry.value)}
        </p>
      ))}
    </div>
  );
}

const tooltipStyles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: 'white',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
    margin: 0,
  },
  value: {
    fontSize: '12px',
    margin: '4px 0',
  },
};

/**
 * Bot filter dropdown component for filtering expenses by bot.
 */
interface BotFilterProps {
  bots: Array<{ id: string; name: string; icon: string | null }>;
  selectedBotId: string | null;
  onBotChange: (botId: string | null) => void;
  isLoading?: boolean;
}

function BotFilter({ bots, selectedBotId, onBotChange, isLoading }: BotFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedBot = selectedBotId ? bots.find(b => b.id === selectedBotId) : null;
  const displayText = selectedBot ? selectedBot.name : 'All Bots';

  return (
    <div style={botFilterStyles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={botFilterStyles.triggerButton}
        disabled={isLoading}
      >
        <span style={botFilterStyles.icon}>
          {selectedBot?.icon || '🤖'}
        </span>
        <span>{displayText}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{ marginLeft: '8px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div style={botFilterStyles.backdrop} onClick={() => setIsOpen(false)} />
          <div style={botFilterStyles.dropdown}>
            <button
              onClick={() => { onBotChange(null); setIsOpen(false); }}
              style={{
                ...botFilterStyles.option,
                ...(selectedBotId === null ? botFilterStyles.optionActive : {}),
              }}
            >
              <span style={botFilterStyles.optionIcon}>🌐</span>
              <span>All Bots</span>
              {selectedBotId === null && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 'auto' }}>
                  <path d="M2 7L5.5 10.5L12 4" stroke="#5A8A5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <div style={botFilterStyles.divider} />
            {bots.map((bot) => (
              <button
                key={bot.id}
                onClick={() => { onBotChange(bot.id); setIsOpen(false); }}
                style={{
                  ...botFilterStyles.option,
                  ...(selectedBotId === bot.id ? botFilterStyles.optionActive : {}),
                }}
              >
                <span style={botFilterStyles.optionIcon}>{bot.icon || '🤖'}</span>
                <span>{bot.name}</span>
                {selectedBotId === bot.id && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 'auto' }}>
                    <path d="M2 7L5.5 10.5L12 4" stroke="#5A8A5E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const botFilterStyles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  triggerButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    backgroundColor: 'white',
    color: '#3D3A36',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    gap: '8px',
  },
  icon: {
    fontSize: '16px',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.12)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    zIndex: 100,
    minWidth: '200px',
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '8px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#5C564E',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease',
    gap: '8px',
  },
  optionActive: {
    backgroundColor: 'rgba(90, 138, 94, 0.08)',
    color: '#5A8A5E',
    fontWeight: 600,
  },
  optionIcon: {
    fontSize: '14px',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(93, 86, 78, 0.08)',
    margin: '8px 0',
  },
};

/**
 * API Expenses Dashboard page component.
 *
 * Displays aggregated expense data via tRPC with time series and distribution charts.
 * Shows global expenses by default with ability to drill down by bot.
 */
export function ApiExpensesPage() {
  const { bots, isLoading: botsLoading } = useProject();

  // Bot filter state (null = all bots)
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);

  // Date range state
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('last30days');
  const [customDateRange, setCustomDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const monthAgo = new Date(now);
    monthAgo.setDate(monthAgo.getDate() - 30);
    return {
      startDate: monthAgo.toISOString().split('T')[0],
      endDate: today,
    };
  });

  // Compute current date range
  const dateRange = useMemo(
    () => getDateRangeFromPreset(dateRangePreset, customDateRange),
    [dateRangePreset, customDateRange]
  );

  // Fetch aggregated data by day (time series)
  // When selectedBotId is null, query all bots (global view)
  const timeSeriesQuery = trpc.analytics.expenses.getAggregated.useQuery(
    {
      groupBy: ['day'],
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }
  );

  // Fetch aggregated data by model (distribution)
  const modelDistributionQuery = trpc.analytics.expenses.getAggregated.useQuery(
    {
      groupBy: ['model'],
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }
  );

  // Fetch aggregated data by call type
  const callTypeDistributionQuery = trpc.analytics.expenses.getAggregated.useQuery(
    {
      groupBy: ['callType'],
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }
  );

  // Fetch aggregated data by modality
  const modalityDistributionQuery = trpc.analytics.expenses.getAggregated.useQuery(
    {
      groupBy: ['modality'],
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }
  );

  // Fetch aggregated data by bot (only when no specific bot is selected)
  const botDistributionQuery = trpc.analytics.expenses.getAggregated.useQuery(
    {
      groupBy: ['bot'],
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    },
    {
      enabled: selectedBotId === null,
    }
  );

  // Fetch leaderboard data (top users by API cost)
  const leaderboardQuery = trpc.analytics.expenses.getLeaderboard.useQuery(
    {
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      limit: 10,
    }
  );

  // Fetch profitability data
  const profitabilityQuery = trpc.analytics.expenses.getProfitability.useQuery(
    {
      botId: selectedBotId ?? undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      limit: 50,
    }
  );

  // Loading state
  const isLoading = timeSeriesQuery.isLoading || modelDistributionQuery.isLoading ||
    callTypeDistributionQuery.isLoading || modalityDistributionQuery.isLoading ||
    leaderboardQuery.isLoading || profitabilityQuery.isLoading;

  // Error state
  const error = timeSeriesQuery.error || modelDistributionQuery.error ||
    callTypeDistributionQuery.error || modalityDistributionQuery.error ||
    leaderboardQuery.error || profitabilityQuery.error;

  // Get summary from any query (they should have the same summary)
  const summary = timeSeriesQuery.data?.summary;

  // Prepare time series data for charts
  // Note: useMemo hooks must be called before any early returns to follow Rules of Hooks
  const timeSeriesData = useMemo(() => {
    if (!timeSeriesQuery.data?.data) return [];
    return timeSeriesQuery.data.data
      .filter(d => d.period)
      .map(d => ({
        date: d.period!,
        displayDate: new Date(d.period! + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cost: d.totalCostCents,
        tokens: d.totalTokens,
        calls: d.totalCalls,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [timeSeriesQuery.data]);

  // Prepare model distribution data for pie chart
  const modelData = useMemo(() => {
    if (!modelDistributionQuery.data?.data) return [];
    return modelDistributionQuery.data.data
      .filter(d => d.model)
      .map((d, i) => ({
        name: d.model!,
        value: d.totalCostCents,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [modelDistributionQuery.data]);

  // Prepare call type distribution data
  const callTypeData = useMemo(() => {
    if (!callTypeDistributionQuery.data?.data) return [];
    return callTypeDistributionQuery.data.data
      .filter(d => d.callType)
      .map((d, i) => ({
        name: d.callType!,
        value: d.totalCostCents,
        tokens: d.totalTokens,
        calls: d.totalCalls,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [callTypeDistributionQuery.data]);

  // Prepare modality distribution data
  const modalityData = useMemo(() => {
    if (!modalityDistributionQuery.data?.data) return [];
    return modalityDistributionQuery.data.data
      .filter(d => d.modality)
      .map((d, i) => ({
        name: d.modality!,
        value: d.totalCostCents,
        tokens: d.totalTokens,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [modalityDistributionQuery.data]);

  // Prepare bot distribution data (for pie chart when no bot selected)
  const botData = useMemo(() => {
    if (!botDistributionQuery.data?.data) return [];
    return botDistributionQuery.data.data
      .filter(d => d.botId)
      .map((d, i) => ({
        id: d.botId!,
        name: d.botName || 'Unknown Bot',
        icon: d.botIcon || '🤖',
        value: d.totalCostCents,
        tokens: d.totalTokens,
        calls: d.totalCalls,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
      .sort((a, b) => b.value - a.value);
  }, [botDistributionQuery.data]);

  // Calculate total cost for percentage calculation
  const totalBotCost = useMemo(() => {
    return botData.reduce((sum, bot) => sum + bot.value, 0);
  }, [botData]);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading expense data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          Error loading expense data: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>API Expenses</h1>
          <p style={styles.subtitle}>
            Track AI API costs and usage patterns
            {selectedBotId && bots.find(b => b.id === selectedBotId) && (
              <span style={{ marginLeft: '8px', fontWeight: 500, color: '#5A8A5E' }}>
                - {bots.find(b => b.id === selectedBotId)?.name}
              </span>
            )}
          </p>
        </div>
        <div style={styles.headerActions}>
          <BotFilter
            bots={bots}
            selectedBotId={selectedBotId}
            onBotChange={setSelectedBotId}
            isLoading={botsLoading}
          />
          <DateRangePicker
            selectedPreset={dateRangePreset}
            customRange={customDateRange}
            onPresetChange={setDateRangePreset}
            onCustomRangeChange={setCustomDateRange}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <span style={styles.metricIcon}>💰</span>
          </div>
          <p style={styles.metricValue}>{formatCost(summary?.totalCostCents ?? 0)}</p>
          <p style={styles.metricLabel}>Total Cost</p>
          <p style={styles.metricSubtext}>
            ${((summary?.totalCostCents ?? 0) / 100).toFixed(2)} exact
          </p>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <span style={styles.metricIcon}>🔢</span>
          </div>
          <p style={styles.metricValue}>{formatTokens(summary?.totalTokens ?? 0)}</p>
          <p style={styles.metricLabel}>Total Tokens</p>
          <p style={styles.metricSubtext}>
            In: {formatTokens(summary?.totalInputTokens ?? 0)} / Out: {formatTokens(summary?.totalOutputTokens ?? 0)}
          </p>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <span style={styles.metricIcon}>📞</span>
          </div>
          <p style={styles.metricValue}>{(summary?.totalCalls ?? 0).toLocaleString()}</p>
          <p style={styles.metricLabel}>API Calls</p>
          <p style={styles.metricSubtext}>
            Avg: {formatCost(summary?.avgCostPerCallCents ?? 0)}/call
          </p>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricHeader}>
            <span style={styles.metricIcon}>🏆</span>
          </div>
          <p style={styles.metricValue}>{summary?.topModel ?? '-'}</p>
          <p style={styles.metricLabel}>Top Model</p>
          <p style={styles.metricSubtext}>
            Type: {summary?.topCallType ?? '-'}
          </p>
        </div>
      </div>

      {/* Cost Over Time Chart */}
      <div style={styles.section}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Cost Over Time</h3>
          <p style={styles.chartSubtitle}>Daily API expenditure</p>
        </div>
        {timeSeriesData.length > 0 ? (
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeriesData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5A8A5E" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#5A8A5E" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="displayDate"
                  tick={{ fontSize: 11, fill: '#9B9285' }}
                  axisLine={{ stroke: 'rgba(93, 86, 78, 0.08)' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatCost(v)}
                  tick={{ fontSize: 11, fill: '#9B9285' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cost"
                  name="Cost"
                  stroke="#5A8A5E"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorCost)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={styles.emptyChart}>No data available for this period</div>
        )}
      </div>

      {/* Token Usage Over Time */}
      <div style={styles.section}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Token Usage Over Time</h3>
          <p style={styles.chartSubtitle}>Daily token consumption</p>
        </div>
        {timeSeriesData.length > 0 ? (
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeSeriesData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="displayDate"
                  tick={{ fontSize: 11, fill: '#9B9285' }}
                  axisLine={{ stroke: 'rgba(93, 86, 78, 0.08)' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatTokens(v)}
                  tick={{ fontSize: 11, fill: '#9B9285' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip valueFormatter={formatTokens} />} />
                <Line
                  type="monotone"
                  dataKey="tokens"
                  name="Tokens"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={styles.emptyChart}>No data available for this period</div>
        )}
      </div>

      {/* Cost by Bot Section - shown only when no specific bot is selected */}
      {selectedBotId === null && botData.length > 0 && (
        <div style={styles.section}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Cost by Bot</h3>
            <p style={styles.chartSubtitle}>API cost distribution across all bots</p>
          </div>
          <div style={{ display: 'flex', gap: '32px' }}>
            {/* Pie Chart */}
            <div style={{ width: 240, height: 240, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={botData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {botData.map((entry, index) => (
                      <Cell key={`bot-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Bot List */}
            <div style={styles.botListContainer}>
              {botData.map((bot, index) => {
                const percentage = totalBotCost > 0 ? (bot.value / totalBotCost) * 100 : 0;
                return (
                  <div
                    key={bot.id}
                    style={styles.botListItem}
                    onClick={() => setSelectedBotId(bot.id)}
                  >
                    <div style={styles.botListRank}>
                      <span style={{
                        ...styles.rankBadge,
                        ...(index === 0 ? styles.rankBadgeGold : {}),
                        ...(index === 1 ? styles.rankBadgeSilver : {}),
                        ...(index === 2 ? styles.rankBadgeBronze : {}),
                      }}>
                        #{index + 1}
                      </span>
                    </div>
                    <div style={styles.botListIcon}>{bot.icon}</div>
                    <div style={styles.botListInfo}>
                      <span style={styles.botListName}>{bot.name}</span>
                      <span style={styles.botListMeta}>
                        {bot.calls.toLocaleString()} calls · {formatTokens(bot.tokens)} tokens
                      </span>
                    </div>
                    <div style={styles.botListCost}>
                      <span style={styles.botListCostValue}>{formatCost(bot.value)}</span>
                      <span style={styles.botListCostPercent}>{percentage.toFixed(1)}%</span>
                    </div>
                    <div style={{ ...styles.botListColorBar, backgroundColor: bot.color }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Distribution Charts Row */}
      <div style={styles.chartsRow}>
        {/* Cost by Model */}
        <div style={styles.chartCard}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Cost by Model</h3>
            <p style={styles.chartSubtitle}>Distribution across AI models</p>
          </div>
          {modelData.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <div style={{ width: 200, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={modelData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {modelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={styles.legendContainer}>
                {modelData.slice(0, 5).map((entry, index) => (
                  <div key={index} style={styles.legendItem}>
                    <div style={{ ...styles.legendDot, backgroundColor: entry.color }} />
                    <span style={styles.legendLabel}>{entry.name}</span>
                    <span style={styles.legendValue}>{formatCost(entry.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={styles.emptyChart}>No model data available</div>
          )}
        </div>

        {/* Cost by Call Type */}
        <div style={styles.chartCard}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Cost by Call Type</h3>
            <p style={styles.chartSubtitle}>Breakdown by API call type</p>
          </div>
          {callTypeData.length > 0 ? (
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={callTypeData} layout="vertical" margin={{ left: 80, right: 30 }}>
                  <XAxis
                    type="number"
                    tickFormatter={(v) => formatCost(v)}
                    tick={{ fontSize: 11, fill: '#9B9285' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#3D3A36' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Cost" radius={[0, 4, 4, 0]}>
                    {callTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={styles.emptyChart}>No call type data available</div>
          )}
        </div>
      </div>

      {/* Cost by Modality */}
      <div style={styles.section}>
        <div style={styles.chartHeaderSimple}>
          <h3 style={styles.chartTitle}>Cost by Modality</h3>
          <p style={styles.chartSubtitle}>Distribution across input/output types</p>
        </div>
        {modalityData.length > 0 ? (
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={modalityData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#3D3A36' }}
                  axisLine={{ stroke: 'rgba(93, 86, 78, 0.08)' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatCost(v)}
                  tick={{ fontSize: 11, fill: '#9B9285' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="value" name="Cost" radius={[4, 4, 0, 0]}>
                  {modalityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={styles.emptyChart}>No modality data available</div>
        )}
        {modalityData.length > 0 && (
          <div style={styles.chartFooter}>
            {modalityData.map((entry, index) => (
              <div key={index} style={styles.footerStat}>
                <div style={{ ...styles.footerDot, backgroundColor: entry.color }} />
                <span style={styles.footerLabel}>{entry.name}:</span>
                <span style={styles.footerValue}>{formatTokens(entry.tokens)} tokens</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profitability Summary Cards */}
      {profitabilityQuery.data?.summary && (
        <div style={styles.section}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Profitability Overview</h3>
            <p style={styles.chartSubtitle}>Revenue vs. API costs analysis</p>
          </div>
          <div style={styles.profitabilityGrid}>
            <div style={styles.profitCard}>
              <div style={styles.profitCardHeader}>
                <span style={styles.profitCardIcon}>💵</span>
                <span style={styles.profitCardLabel}>Monthly Revenue</span>
              </div>
              <p style={styles.profitCardValue}>
                ${profitabilityQuery.data.summary.totalMonthlyRevenueDollars.toFixed(2)}
              </p>
              <p style={styles.profitCardSubtext}>
                {profitabilityQuery.data.summary.totalUsers} users
              </p>
            </div>

            <div style={styles.profitCard}>
              <div style={styles.profitCardHeader}>
                <span style={styles.profitCardIcon}>🔥</span>
                <span style={styles.profitCardLabel}>API Costs</span>
              </div>
              <p style={styles.profitCardValue}>
                ${profitabilityQuery.data.summary.totalCostDollars.toFixed(2)}
              </p>
              <p style={styles.profitCardSubtext}>
                Avg: ${(profitabilityQuery.data.summary.totalCostCents / Math.max(profitabilityQuery.data.summary.totalUsers, 1) / 100).toFixed(2)}/user
              </p>
            </div>

            <div style={{
              ...styles.profitCard,
              borderColor: profitabilityQuery.data.summary.totalGrossProfitCents >= 0 ? 'rgba(90, 138, 94, 0.3)' : 'rgba(139, 90, 80, 0.3)',
            }}>
              <div style={styles.profitCardHeader}>
                <span style={styles.profitCardIcon}>
                  {profitabilityQuery.data.summary.totalGrossProfitCents >= 0 ? '📈' : '📉'}
                </span>
                <span style={styles.profitCardLabel}>Gross Profit</span>
              </div>
              <p style={{
                ...styles.profitCardValue,
                color: profitabilityQuery.data.summary.totalGrossProfitCents >= 0 ? '#5A8A5E' : '#8B5A50',
              }}>
                ${profitabilityQuery.data.summary.totalGrossProfitDollars.toFixed(2)}
              </p>
              <p style={styles.profitCardSubtext}>
                Avg: ${(profitabilityQuery.data.summary.avgProfitPerUserCents / 100).toFixed(2)}/user
              </p>
            </div>

            <div style={{
              ...styles.profitCard,
              borderColor: profitabilityQuery.data.summary.overallProfitMarginPercent >= 0 ? 'rgba(90, 138, 94, 0.3)' : 'rgba(139, 90, 80, 0.3)',
            }}>
              <div style={styles.profitCardHeader}>
                <span style={styles.profitCardIcon}>📊</span>
                <span style={styles.profitCardLabel}>Profit Margin</span>
              </div>
              <p style={{
                ...styles.profitCardValue,
                color: profitabilityQuery.data.summary.overallProfitMarginPercent >= 0 ? '#5A8A5E' : '#8B5A50',
              }}>
                {profitabilityQuery.data.summary.overallProfitMarginPercent.toFixed(1)}%
              </p>
              <div style={styles.profitUserStats}>
                <span style={styles.profitUserBadgeGreen}>
                  {profitabilityQuery.data.summary.profitableUsers} profitable
                </span>
                <span style={styles.profitUserBadgeRed}>
                  {profitabilityQuery.data.summary.unprofitableUsers} unprofitable
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Leaderboard Table */}
      {leaderboardQuery.data?.data && leaderboardQuery.data.data.length > 0 && (
        <div style={styles.section}>
          <div style={styles.chartHeaderSimple}>
            <h3 style={styles.chartTitle}>Cost Leaderboard</h3>
            <p style={styles.chartSubtitle}>Top users ranked by API cost</p>
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.leaderboardTable}>
              <thead>
                <tr>
                  <th style={styles.tableHeaderRank}>Rank</th>
                  <th style={styles.tableHeaderLeft}>User</th>
                  <th style={styles.tableHeader}>Platform</th>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeaderRight}>Cost</th>
                  <th style={styles.tableHeaderRight}>Calls</th>
                  <th style={styles.tableHeaderRight}>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardQuery.data.data.map((entry) => (
                  <tr key={entry.userId} style={styles.tableRow}>
                    <td style={styles.tableCellRank}>
                      <span style={{
                        ...styles.rankBadge,
                        ...(entry.rank === 1 ? styles.rankBadgeGold : {}),
                        ...(entry.rank === 2 ? styles.rankBadgeSilver : {}),
                        ...(entry.rank === 3 ? styles.rankBadgeBronze : {}),
                      }}>
                        #{entry.rank}
                      </span>
                    </td>
                    <td style={styles.tableCellUser}>
                      <span style={styles.userName}>{entry.userName || 'Anonymous'}</span>
                      {entry.country && (
                        <span style={styles.userCountry}>{entry.country}</span>
                      )}
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.platformBadge}>{entry.platform}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(entry.subscriptionStatus === 'active' ? styles.statusBadgeActive : {}),
                        ...(entry.subscriptionStatus === 'canceled' ? styles.statusBadgeCanceled : {}),
                        ...(entry.subscriptionStatus === 'trialing' ? styles.statusBadgeTrialing : {}),
                      }}>
                        {entry.subscriptionStatus || 'none'}
                      </span>
                    </td>
                    <td style={styles.tableCellRight}>
                      <span style={styles.costValue}>${entry.totalCostDollars.toFixed(2)}</span>
                    </td>
                    <td style={styles.tableCellRight}>
                      {entry.totalCalls.toLocaleString()}
                    </td>
                    <td style={styles.tableCellRight}>
                      {formatTokens(entry.totalTokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {leaderboardQuery.data.summary && (
            <div style={styles.leaderboardFooter}>
              <div style={styles.leaderboardStat}>
                <span style={styles.leaderboardStatLabel}>Total Users:</span>
                <span style={styles.leaderboardStatValue}>{leaderboardQuery.data.summary.totalUsers}</span>
              </div>
              <div style={styles.leaderboardStat}>
                <span style={styles.leaderboardStatLabel}>Combined Cost:</span>
                <span style={styles.leaderboardStatValue}>${leaderboardQuery.data.summary.totalCostDollars.toFixed(2)}</span>
              </div>
              <div style={styles.leaderboardStat}>
                <span style={styles.leaderboardStatLabel}>Avg Cost/User:</span>
                <span style={styles.leaderboardStatValue}>${(leaderboardQuery.data.summary.avgCostPerUserCents / 100).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '28px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
  },
  error: {
    padding: '16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '12px',
  },
  emptyState: {
    padding: '60px 40px',
    textAlign: 'center',
    background: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  emptyChart: {
    padding: '40px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '13px',
  },
  // Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  metricIcon: {
    fontSize: '20px',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  metricLabel: {
    fontSize: '12px',
    color: '#9B9285',
    marginBottom: '2px',
    margin: 0,
  },
  metricSubtext: {
    fontSize: '11px',
    color: '#706A62',
    margin: 0,
  },
  // Section / Chart Card
  section: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
    marginBottom: '24px',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '24px',
  },
  chartCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  chartHeaderSimple: {
    marginBottom: '20px',
  },
  chartTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    margin: 0,
  },
  chartSubtitle: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  // Legend
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: '12px',
    color: '#5C564E',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  legendValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  // Footer
  chartFooter: {
    display: 'flex',
    gap: '24px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
    flexWrap: 'wrap',
  },
  footerStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  footerDot: {
    width: '8px',
    height: '8px',
    borderRadius: '2px',
  },
  footerLabel: {
    fontSize: '13px',
    color: '#9B9285',
  },
  footerValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  // Profitability cards
  profitabilityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  profitCard: {
    background: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  profitCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  profitCardIcon: {
    fontSize: '18px',
  },
  profitCardLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#9B9285',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  profitCardValue: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '4px',
    margin: 0,
  },
  profitCardSubtext: {
    fontSize: '12px',
    color: '#706A62',
    margin: 0,
  },
  profitUserStats: {
    display: 'flex',
    gap: '8px',
    marginTop: '4px',
  },
  profitUserBadgeGreen: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#5A8A5E',
    backgroundColor: 'rgba(90, 138, 94, 0.1)',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  profitUserBadgeRed: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#8B5A50',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  // Leaderboard table
  tableContainer: {
    overflowX: 'auto' as const,
  },
  leaderboardTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  tableHeaderRank: {
    padding: '12px 16px',
    textAlign: 'center' as const,
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    width: '70px',
  },
  tableHeaderLeft: {
    padding: '12px 16px',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  tableHeader: {
    padding: '12px 16px',
    textAlign: 'center' as const,
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  tableHeaderRight: {
    padding: '12px 16px',
    textAlign: 'right' as const,
    fontWeight: 600,
    color: '#5C564E',
    background: 'rgba(93, 86, 78, 0.03)',
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  tableRow: {
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
  },
  tableCellRank: {
    padding: '14px 16px',
    textAlign: 'center' as const,
  },
  tableCellUser: {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  tableCell: {
    padding: '14px 16px',
    textAlign: 'center' as const,
    color: '#5C564E',
  },
  tableCellRight: {
    padding: '14px 16px',
    textAlign: 'right' as const,
    color: '#5C564E',
  },
  rankBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: 'rgba(93, 86, 78, 0.08)',
    color: '#5C564E',
  },
  rankBadgeGold: {
    backgroundColor: 'rgba(155, 123, 60, 0.15)',
    color: '#9B7B3C',
  },
  rankBadgeSilver: {
    backgroundColor: 'rgba(155, 146, 133, 0.2)',
    color: '#706A62',
  },
  rankBadgeBronze: {
    backgroundColor: 'rgba(201, 160, 144, 0.2)',
    color: '#9B7B6C',
  },
  userName: {
    fontWeight: 500,
    color: '#3D3A36',
  },
  userCountry: {
    fontSize: '11px',
    color: '#9B9285',
  },
  platformBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    color: '#6366f1',
    textTransform: 'capitalize' as const,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: 'rgba(93, 86, 78, 0.08)',
    color: '#9B9285',
    textTransform: 'capitalize' as const,
  },
  statusBadgeActive: {
    backgroundColor: 'rgba(90, 138, 94, 0.1)',
    color: '#5A8A5E',
  },
  statusBadgeCanceled: {
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
  },
  statusBadgeTrialing: {
    backgroundColor: 'rgba(155, 123, 60, 0.1)',
    color: '#9B7B3C',
  },
  costValue: {
    fontWeight: 600,
    color: '#3D3A36',
  },
  leaderboardFooter: {
    display: 'flex',
    gap: '32px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
  },
  leaderboardStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  leaderboardStatLabel: {
    fontSize: '12px',
    color: '#9B9285',
  },
  leaderboardStatValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  // Bot Distribution List Styles
  botListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    maxHeight: '280px',
    overflowY: 'auto' as const,
  },
  botListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  botListRank: {
    width: '48px',
    flexShrink: 0,
  },
  botListIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  botListInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    minWidth: 0,
  },
  botListName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  botListMeta: {
    fontSize: '12px',
    color: '#9B9285',
  },
  botListCost: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '2px',
    flexShrink: 0,
  },
  botListCostValue: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  botListCostPercent: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#9B9285',
  },
  botListColorBar: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    borderRadius: '4px 0 0 4px',
  },
};

export default ApiExpensesPage;
